import { Component, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EventResponse, OrderResponse, CompanyResponse } from '../../../../../../../../@shared/models/response.module';
import { PaymentService } from '../../../../../../../../@shared/services/payment.service';
import { EventsService } from '../../../../../../../../@shared/services/events-service';
import { EndpointService } from '../../../../../../../../@shared/endpoints.service';
import { formatDateDMYDots } from '../../../../../../../../@core/utils/utils.service';
import { ViewOrderDetails } from './components/view-order-details/view-order-details';
import { RefundOrderModal } from './components/refund-order-modal/refund-order-modal';
import { _AQH_EVE_2, AQH_EVE_152 } from '../../../../../../../../@shared/models/input.module';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../../../../../@core/services/toast.service';

interface OrderStats {
  totalOrders: number;
  paidOrders: number;
  pendingOrders: number;
  failedOrders: number;
  refundedOrders: number;
  totalRevenue: number;
  pendingRevenue: number;
  totalFees: number;
  netPayout: number;
  currency: string;
}

@Component({
  selector: 'app-manage-event-orders',
  standalone: false,
  templateUrl: './manage-event-orders.html',
  styleUrl: './manage-event-orders.css'
})
export class ManageEventOrders implements OnInit {
  @Input() event: EventResponse;

  orders: OrderResponse[] = [];
  filteredOrders: OrderResponse[] = [];
  loading: boolean = true;
  stats: OrderStats = {
    totalOrders: 0,
    paidOrders: 0,
    pendingOrders: 0,
    failedOrders: 0,
    refundedOrders: 0,
    totalRevenue: 0,
    pendingRevenue: 0,
    totalFees: 0,
    netPayout: 0,
    currency: 'USD'
  };

  // Filters
  statusFilter: string = 'all';
  searchQuery: string = '';

  // Payment settings
  savingPaymentSettings: boolean = false;

  constructor(
    private paymentService: PaymentService,
    private eventService: EventsService,
    private endpointService: EndpointService,
    private modalService: NgbModal,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.loadOrders();
  }

  loadOrders() {
    this.loading = true;
    this.paymentService.getOrders({ event_id: this.event.event_id }).subscribe({
      next: (orders) => {
        this.orders = orders;
        this.calculateStats();
        this.applyFilters();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  calculateStats() {
    const paidOrders = this.orders.filter(o => o.status === 'paid');
    const totalRevenue = paidOrders.reduce((sum, o) => sum + o.total_cents, 0);
    const totalFees = paidOrders.reduce((sum, o) => sum + (o.platform_fee_cents || 0), 0);

    this.stats = {
      totalOrders: this.orders.length,
      paidOrders: paidOrders.length,
      pendingOrders: this.orders.filter(o => o.status === 'pending').length,
      failedOrders: this.orders.filter(o => o.status === 'failed').length,
      refundedOrders: this.orders.filter(o => o.status === 'refunded').length,
      totalRevenue: totalRevenue,
      pendingRevenue: this.orders
        .filter(o => o.status === 'pending')
        .reduce((sum, o) => sum + o.total_cents, 0),
      totalFees: totalFees,
      netPayout: totalRevenue - totalFees,
      currency: this.orders.length > 0 ? this.orders[0].currency : 'NOK'
    };
  }

  applyFilters() {
    let filtered = [...this.orders];

    // Status filter
    if (this.statusFilter !== 'all') {
      filtered = filtered.filter(o => o.status === this.statusFilter);
    }

    // Search filter
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase().trim();
      filtered = filtered.filter(o => {
        const userName = o.user ? `${o.user.first_name} ${o.user.last_name}`.toLowerCase() : '';
        const email = o.user?.email?.toLowerCase() || '';
        const orderId = o.order_id.toString();
        return userName.includes(query) || email.includes(query) || orderId.includes(query);
      });
    }

    // Sort by created_at descending (newest first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return dateB - dateA;
    });

    this.filteredOrders = filtered;
  }

  onStatusFilterChange(status: string) {
    this.statusFilter = status;
    this.applyFilters();
  }

  onSearchChange(query: string) {
    this.searchQuery = query;
    this.applyFilters();
  }

  formatCents(cents: number): string {
    return (cents / 100).toFixed(2);
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '-';
    return formatDateDMYDots(dateStr);
  }

  formatDateTime(dateStr: string | null | undefined): string {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'paid':
        return 'badge-light-success';
      case 'pending':
        return 'badge-light-warning';
      case 'failed':
        return 'badge-light-danger';
      case 'refunded':
        return 'badge-light-info';
      case 'cancelled':
        return 'badge-light-secondary';
      default:
        return 'badge-light-secondary';
    }
  }

  getStatusLabel(status: string): string {
    const statusMap: { [key: string]: string } = {
      'paid': 'manageEvent.orders.statusPaid',
      'pending': 'manageEvent.orders.statusPending',
      'failed': 'manageEvent.orders.statusFailed',
      'refunded': 'manageEvent.orders.statusRefunded',
      'cancelled': 'manageEvent.orders.statusCancelled'
    };
    return this.translateService.instant(statusMap[status] || 'manageEvent.orders.statusPending');
  }

  getUserDisplayName(order: OrderResponse): string {
    if (order.user) {
      return `${order.user.first_name || ''} ${order.user.last_name || ''}`.trim() || 'Unknown';
    }
    return 'Unknown';
  }

  getUserEmail(order: OrderResponse): string {
    return order.user?.email || '';
  }

  getOrderLinesCount(order: OrderResponse): number {
    return order.order_lines?.length || 0;
  }

  getOrderNetPayout(order: OrderResponse): number {
    return order.total_cents - (order.platform_fee_cents || 0);
  }

  viewOrderDetails(order: OrderResponse) {
    const modalRef = this.modalService.open(ViewOrderDetails, { size: 'lg', centered: true });
    modalRef.componentInstance.order = order;
    modalRef.componentInstance.event = this.event;
  }

  openRefundModal(order: OrderResponse) {
    const modalRef = this.modalService.open(RefundOrderModal, { size: 'md', centered: true });
    modalRef.componentInstance.order = order;

    modalRef.result.then(
      (result) => {
        // Refund successful - reload orders
        this.loadOrders();
      },
      () => {
        // Modal dismissed
      }
    );
  }

  canRefund(order: OrderResponse): boolean {
    // Can only refund paid orders that were paid via Stripe
    return order.status === 'paid' && !!order.stripe_payment_intent_id;
  }

  hasPartialRefund(order: OrderResponse): boolean {
    return (order.total_refunded_cents || 0) > 0 && order.status !== 'refunded';
  }

  // Payment settings methods
  get company(): CompanyResponse | null | undefined {
    return this.event?.company;
  }

  get isStripeConnected(): boolean {
    return this.company?.stripe_account_status === 'active';
  }

  get isCustomPaymentEnabled(): boolean {
    return this.company?.custom_payment_enabled === true;
  }

  get canConfigurePayment(): boolean {
    return this.isStripeConnected || this.isCustomPaymentEnabled;
  }

  togglePaymentEnabled(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    const enabled = checkbox.checked;
    // Default to pay_later if custom payment is enabled without Stripe (immediate requires Stripe)
    let timing: 'immediate' | 'pay_later';
    if (this.isCustomPaymentEnabled && !this.isStripeConnected) {
      timing = 'pay_later';
    } else {
      timing = (this.event.payment_timing === 'pay_later' ? 'pay_later' : 'immediate');
    }
    this.updatePaymentSettings(enabled, timing);
  }

  setPaymentTiming(timing: 'immediate' | 'pay_later') {
    this.updatePaymentSettings(this.event.payment_enabled || false, timing);
  }

  private updatePaymentSettings(paymentEnabled: boolean, paymentTiming: 'immediate' | 'pay_later') {
    this.savingPaymentSettings = true;

    const params: _AQH_EVE_2 = {
      event_id: this.event.event_id,
      payment_enabled: paymentEnabled,
      payment_timing: paymentTiming
    };

    this.eventService.editEvent(params).subscribe({
      next: () => {
        this.event.payment_enabled = paymentEnabled;
        this.event.payment_timing = paymentTiming;
        this.savingPaymentSettings = false;
        this.toastService.success(this.translateService.instant('manageEvent.orders.paymentSettingsSuccess'));
      },
      error: (err: any) => {
        console.error('Failed to update payment settings:', err);
        this.savingPaymentSettings = false;
        this.toastService.error(err.error?.reason || this.translateService.instant('manageEvent.orders.paymentSettingsError'));
      }
    });
  }

  markOrderAsPaid(order: OrderResponse, paymentMethod: 'cash' | 'bank_transfer' | 'other') {
    if (!this.event.company_id) {
      this.toastService.error(this.translateService.instant('manageEvent.orders.markPaidError'));
      return;
    }

    const payload: AQH_EVE_152 = {
      company_id: this.event.company_id,
      order_id: order.order_id,
      payment_method: paymentMethod
    };

    this.endpointService.aqh_eve_152(payload).subscribe({
      next: () => {
        this.toastService.success(this.translateService.instant('manageEvent.orders.markPaidSuccess'));
        // Reload orders to reflect the change
        this.loadOrders();
      },
      error: () => {
        this.toastService.error(this.translateService.instant('manageEvent.orders.markPaidError'));
      }
    });
  }

  protected readonly formatDateDMYDots = formatDateDMYDots;
}
